import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getOrganizationById,
  getOrganizationTypes,
  getOrganizations,
  updateOrganization,
  linkOrganizationToLocation,
  unlinkOrganizationFromLocation,
  getCanadaProvinces,
  getCanadaMunicipalities,
  getCanadaNeighbourhoods
} from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Building2,
  Save,
  AlertCircle,
  Globe,
  Share2,
  FileEdit,
} from 'lucide-react';
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { toast } from '../../hooks/use-toast';
import { SocialLinksEditor } from './SocialLinksEditor';
import {
  OrganizationFormData,
  SocialLink,
  SchoolHours,
  DEFAULT_SCHOOL_HOURS,
  DAYS_OF_WEEK,
  FORMAT_OPTIONS,
  PROGRAMS_OFFERED_OPTIONS,
  AGE_RANGE_OPTIONS
} from './organizationTypes';

interface FormErrors {
  name?: string;
  slug?: string;
  organizationTypeId?: string;
  submit?: string;
}

const DIRECTORY_SECTIONS = [
  { key: 'tenants', label: 'Tenants' },
  { key: 'food-court', label: 'Food Court' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'services', label: 'Services' }
];

const COMMUNITY_SECTIONS = [
  { key: 'notices', label: 'Notices' },
  { key: 'events', label: 'Events / Activities' },
  { key: 'buy-sell', label: 'Buy & Sell' },
  { key: 'request-offer', label: 'Request & Offer' },
  { key: 'lost-found', label: 'Lost & Found' }
];

const ALL_SIDEBAR_SECTIONS = [...DIRECTORY_SECTIONS, ...COMMUNITY_SECTIONS];

const FormSection = ({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
    <div className="pb-1 border-b border-gray-100">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
    </div>
    {children}
  </div>
);

export default function EditOrganizationPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const { locations } = useLocationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [activeTab, setActiveTab] = useState<'details' | 'location' | 'services' | 'social' | 'sidebar'>('details');

  // Location selection state
  const [selectedProvinceId, setSelectedProvinceId] = useState('');
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState('');
  const [selectedNeighbourhoodId, setSelectedNeighbourhoodId] = useState('');
  const [isNational, setIsNational] = useState(false);
  const [locationInitialized, setLocationInitialized] = useState(false);

  const { data: organization, isLoading: isOrganizationLoading, error: organizationError } = useQuery(
    getOrganizationById,
    { id: organizationId! }
  );
  const { data: organizationTypes = [] } = useQuery(getOrganizationTypes);
  const { data: organizations = [] } = useQuery(getOrganizations);
  const { data: provinces = [] } = useQuery(getCanadaProvinces);
  const { data: municipalities = [] } = useQuery(getCanadaMunicipalities, {
    provinceId: selectedProvinceId || undefined
  });
  const { data: neighbourhoods = [] } = useQuery(getCanadaNeighbourhoods, {
    municipalityId: selectedMunicipalityId || undefined
  });

  const updateOrganizationFn = useAction(updateOrganization);
  const linkOrganizationToLocationFn = useAction(linkOrganizationToLocation);
  const unlinkOrganizationFromLocationFn = useAction(unlinkOrganizationFromLocation);

  const [formData, setFormData] = useState<OrganizationFormData>({
    oid: '',
    name: '',
    slug: '',
    organizationTypeId: '',
    parentOrganizationId: '',
    provinceId: '',
    cityId: '',
    address: '',
    postalCode: '',
    contact: '',
    description: '',
    website: '',
    email: '',
    phone: '',
    mediaScope: '',
    subType: '',
    format: '',
    language: '',
    languages: [],
    notes: '',
    isActive: true,
    socialLinks: [],
    serviceDefinitionIds: [],
    hiddenSections: [],
    sidebarOverrideEnabled: false,
    // Type-specific extra fields
    craRegistration: '',
    emergencyPhone: '',
    typeFormat: [],
    licensesAccreditation: '',
    packageTypes: '',
    programsOffered: [],
    ageRange: [],
    durationSchedule: '',
    programType: '',
    schoolHours: { ...DEFAULT_SCHOOL_HOURS },
    CommunityFinanceModel: '',
    shariahCertification: ''
  });

  const buildMediaSettings = (data: OrganizationFormData) => {
    const media = {
      scope: data.mediaScope.trim() || undefined,
      subType: data.subType.trim() || undefined,
      format: data.format.trim() || undefined,
      language: data.language.trim() || undefined,
      notes: data.notes.trim() || undefined
    };
    const hasMedia = Object.values(media).some((value) => value !== undefined);
    return hasMedia ? media : undefined;
  };

  useEffect(() => {
    if (organization) {
      const settings = organization.settings as any;
      const mediaSettings = settings?.media || {};

      setFormData({
        oid: (organization as any).oid || '',
        name: organization.name || '',
        slug: organization.slug || '',
        organizationTypeId: organization.organizationTypeId || '',
        parentOrganizationId: organization.parentOrganizationId || '',
        provinceId: organization.provinceId || '',
        cityId: organization.cityId || '',
        address: organization.address || '',
        postalCode: (organization as any).postalCode || '',
        contact: organization.contact || '',
        description: organization.description || '',
        website: organization.website || '',
        email: (organization as any).email || '',
        phone: (organization as any).phone || '',
        mediaScope: mediaSettings.scope || '',
        subType: mediaSettings.subType || '',
        format: mediaSettings.format || '',
        language: mediaSettings.language || '',
        languages: organization.languages || [],
        notes: mediaSettings.notes || '',
        isActive: organization.isActive !== false,
        socialLinks: settings?.social || [],
        serviceDefinitionIds: settings?.services?.definitionIds || [],
        hiddenSections: settings?.hiddenSections || [],
        sidebarOverrideEnabled: settings != null && 'hiddenSections' in settings,
        // Type-specific extra fields
        craRegistration: settings?.typeFields?.craRegistration || '',
        emergencyPhone: settings?.typeFields?.emergencyPhone || '',
        typeFormat: settings?.typeFields?.format || [],
        licensesAccreditation: settings?.typeFields?.licensesAccreditation || '',
        packageTypes: settings?.typeFields?.packageTypes || '',
        programsOffered: settings?.typeFields?.programsOffered || [],
        ageRange: settings?.typeFields?.ageRange || [],
        durationSchedule: settings?.typeFields?.durationSchedule || '',
        programType: settings?.typeFields?.programType || '',
        schoolHours: settings?.typeFields?.schoolHours || { ...DEFAULT_SCHOOL_HOURS },
        CommunityFinanceModel: settings?.typeFields?.CommunityFinanceModel || '',
        shariahCertification: settings?.typeFields?.shariahCertification || ''
      });

      setIsNational((organization as any).isNational ?? false);
    }
  }, [organization]);

  // Initialize selected province/municipality from stored IDs
  useEffect(() => {
    if (locationInitialized || !organization) return;
    setSelectedProvinceId(organization.provinceId || '');
    setSelectedMunicipalityId(organization.cityId || '');
    setLocationInitialized(true);
  }, [organization, locationInitialized]);

  // Once neighbourhoods load for the matched municipality, pre-select the saved neighbourhood
  // (detected from the org's locationOrganizations — the most specific linked location)
  useEffect(() => {
    if (!selectedMunicipalityId || (neighbourhoods as any[]).length === 0 || !organization) return;
    if (selectedNeighbourhoodId) return; // already set

    const orgLinks: Array<{ locationId: string; location: { id: string; name: string; hierarchyLevel: number } }> =
      (organization as any)?.locationOrganizations ?? [];
    const neighbourhoodLink = orgLinks.find(
      (l) => l.location?.hierarchyLevel === 3 &&
             (neighbourhoods as any[]).some((n: any) => n.name.toLowerCase() === l.location.name.toLowerCase())
    );
    if (neighbourhoodLink) {
      // Find the CanadaNeighbourhood record by name to get its Canada-reference id for the dropdown
      const canadaNbr = (neighbourhoods as any[]).find(
        (n: any) => n.name.toLowerCase() === neighbourhoodLink.location.name.toLowerCase()
      );
      if (canadaNbr) setSelectedNeighbourhoodId(canadaNbr.id);
    }
  }, [neighbourhoods, selectedMunicipalityId, organization]);

  const handleProvinceChange = (provinceId: string) => {
    setSelectedProvinceId(provinceId);
    setSelectedMunicipalityId('');
    setSelectedNeighbourhoodId('');
    setFormData(f => ({ ...f, provinceId: provinceId || '', cityId: '' }));
  };

  const handleMunicipalityChange = (municipalityId: string) => {
    setSelectedMunicipalityId(municipalityId);
    setSelectedNeighbourhoodId('');
    setFormData(f => ({ ...f, cityId: municipalityId || '' }));
  };

  const handleNeighbourhoodChange = (neighbourhoodId: string) => {
    setSelectedNeighbourhoodId(neighbourhoodId);
  };

  // Resolves the most specific Canada reference selection to a real Location.id
  // by matching on name + hierarchyLevel in the Location table (via LocationContext).
  const getSelectedLocationId = (): string | null => {
    const allLocations = locations as any[];

    if (selectedNeighbourhoodId) {
      const nbr = (neighbourhoods as any[]).find((n: any) => n.id === selectedNeighbourhoodId);
      if (nbr) {
        const loc = allLocations.find(
          (l) => l.hierarchyLevel === 3 && l.name.toLowerCase() === nbr.name.toLowerCase()
        );
        if (loc) return loc.id;
      }
    }

    if (selectedMunicipalityId) {
      const mun = (municipalities as any[]).find((m: any) => m.id === selectedMunicipalityId);
      if (mun) {
        const loc = allLocations.find(
          (l) => l.hierarchyLevel === 2 && l.name.toLowerCase() === mun.name.toLowerCase()
        );
        if (loc) return loc.id;
      }
    }

    if (selectedProvinceId) {
      const prov = (provinces as any[]).find((p: any) => p.id === selectedProvinceId);
      if (prov) {
        const loc = allLocations.find(
          (l) => l.hierarchyLevel === 1 && l.name.toLowerCase() === prov.name.toLowerCase()
        );
        if (loc) return loc.id;
      }
    }

    return null;
  };

  const handleNationalToggle = (checked: boolean) => {
    setIsNational(checked);
  };

  const handleChange = (
    field: keyof OrganizationFormData,
    value: string | boolean | string[] | SocialLink[] | SchoolHours
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleMultiToggle = (field: 'typeFormat' | 'programsOffered' | 'ageRange', option: string) => {
    setFormData(prev => {
      const current = prev[field] as string[];
      const updated = current.includes(option)
        ? current.filter(v => v !== option)
        : [...current, option];
      return { ...prev, [field]: updated };
    });
  };

  const handleSchoolHoursChange = (day: string, key: 'enabled' | 'start' | 'end', value: boolean | string) => {
    setFormData(prev => ({
      ...prev,
      schoolHours: {
        ...prev.schoolHours,
        [day]: { ...prev.schoolHours[day], [key]: value }
      }
    }));
  };

  const parentOrganizations = (organizations as any[]).filter((org: any) =>
    org.id !== organizationId && !org.parentOrganizationId
  );

  const selectedType = (organizationTypes as any[]).find((t: any) => t.id === formData.organizationTypeId);
  const isMediaType = selectedType?.key === 'media';
  const isCharityOrSocial = ['charities', 'social-services'].includes(selectedType?.key);
  const isFuneralType = selectedType?.key === 'funeral-services';
  const isMediaPublicationsType = selectedType?.key === 'media-publications';
  const isPilgrimageType = selectedType?.key === 'Pilgrimage-umrah';
  const isWeekendSchoolType = selectedType?.key === 'weekend-schools';
  const isYouthProgramType = selectedType?.key === 'youth-programs';
  const isCommunitySchoolType = selectedType?.key === 'Community-schools';
  const isFinancialType = selectedType?.key === 'financial-services';

  const buildTypeFieldsSettings = () => {
    const tf: any = {};
    if (isCharityOrSocial && formData.craRegistration.trim()) tf.craRegistration = formData.craRegistration.trim();
    if (isFuneralType && formData.emergencyPhone.trim()) tf.emergencyPhone = formData.emergencyPhone.trim();
    if (isMediaPublicationsType && formData.typeFormat.length) tf.format = formData.typeFormat;
    if (isPilgrimageType && formData.licensesAccreditation.trim()) tf.licensesAccreditation = formData.licensesAccreditation.trim();
    if (isPilgrimageType && formData.packageTypes.trim()) tf.packageTypes = formData.packageTypes.trim();
    if (isWeekendSchoolType && formData.programsOffered.length) tf.programsOffered = formData.programsOffered;
    if ((isWeekendSchoolType || isYouthProgramType) && formData.ageRange.length) tf.ageRange = formData.ageRange;
    if (isWeekendSchoolType && formData.durationSchedule.trim()) tf.durationSchedule = formData.durationSchedule.trim();
    if (isYouthProgramType && formData.programType.trim()) tf.programType = formData.programType.trim();
    if (isCommunitySchoolType) tf.schoolHours = formData.schoolHours;
    if (isFinancialType && formData.CommunityFinanceModel.trim()) tf.CommunityFinanceModel = formData.CommunityFinanceModel.trim();
    if (isFinancialType && formData.shariahCertification.trim()) tf.shariahCertification = formData.shariahCertification.trim();
    return Object.keys(tf).length > 0 ? tf : undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Organization name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.organizationTypeId) newErrors.organizationTypeId = 'Organization type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      await updateOrganizationFn({
        id: organizationId!,
        oid: formData.oid.trim() || undefined,
        name: formData.name,
        slug: formData.slug,
        organizationTypeId: formData.organizationTypeId,
        parentOrganizationId: formData.parentOrganizationId || undefined,
        description: formData.description || undefined,
        address: formData.address.trim() || undefined,
        postalCode: formData.postalCode.trim() || undefined,
        website: formData.website.trim() || undefined,
        contact: formData.contact.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        languages: formData.languages,
        provinceId: formData.provinceId || undefined,
        cityId: formData.cityId || undefined,
        isNational,
        media: buildMediaSettings(formData),
        typeFields: buildTypeFieldsSettings(),
        social: formData.socialLinks,
        serviceDefinitionIds: formData.serviceDefinitionIds,
        hiddenSections: formData.sidebarOverrideEnabled ? formData.hiddenSections : null,
        isActive: formData.isActive
      });

      // Sync LocationOrganization: remove all existing province/city/neighbourhood
      // level links, then add the single most-specific one now selected.
      const newLocationId = getSelectedLocationId();
      const existingLinks: Array<{ locationId: string; location: { hierarchyLevel: number } }> =
        (organization as any).locationOrganizations ?? [];

      // Remove any link at hierarchy levels 1-3 (province/city/neighbourhood)
      // These are the ones managed by this form; manually-added links at other levels are untouched.
      for (const link of existingLinks) {
        const level = link.location?.hierarchyLevel ?? -1;
        if (level >= 1 && level <= 3) {
          try {
            await unlinkOrganizationFromLocationFn({ locationId: link.locationId, organizationId: organizationId! });
          } catch (_) { /* already unlinked */ }
        }
      }

      // Link the most specific location selected (neighbourhood > city > province)
      if (newLocationId) {
        try {
          await linkOrganizationToLocationFn({ locationId: newLocationId, organizationId: organizationId! });
        } catch (_) { /* already linked */ }
      }

      toast({ title: 'Success', description: 'Organization updated successfully' });
      navigate(`/admin/organizations/${organizationId}`);
    } catch (error: any) {
      console.error('Error updating organization:', error);
      setErrors({ submit: error.message || 'Failed to update organization. Please try again.' });
      toast({
        title: 'Error',
        description: error.message || 'Failed to update organization',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to edit organizations.</p>
        </div>
      </div>
    );
  }

  if (isOrganizationLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (organizationError || !organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Organization Not Found</h3>
          <p className="text-gray-600 mb-4">The organization you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/organizations')}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Organizations
          </button>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="organization-form"
            disabled={isLoading}
            icon={Save}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </>
            ) : (
              'Update'
            )}
          </ActionButton>
          <ActionButtonSecondary
            onClick={() => navigate(`/admin/organizations/${organizationId}`)}
            disabled={isLoading}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Settings">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
          />
          <span className="text-sm text-gray-700">Active Organization</span>
        </label>
      </SidebarSection>

      <SidebarSection title="Info">
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex justify-between">
            <span className="text-gray-500">Created</span>
            <span>{new Date(organization.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Updated</span>
            <span>{new Date(organization.updatedAt).toLocaleDateString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Sub-Orgs</span>
            <span>{organization._count?.subOrganizations || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Locations</span>
            <span>{organization._count?.locationOrganizations || 0}</span>
          </div>
        </div>
      </SidebarSection>

      <SidebarSection title="Help">
        <div className="space-y-1 text-sm text-gray-600">
          <p>• Name, slug, and type are required</p>
          <p>• Slug must be unique</p>
          <p>• Mark as National to show under Canada at the top level</p>
          <p>• Select province/city to link to a specific location</p>
          <p>• Click platform chips to add social links</p>
          <p>• Select services this organization offers</p>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent} layoutVariant="taxonomy">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Organization</h1>
            <p className="text-gray-600">Editing: {organization.name}</p>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab('details')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Details
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('location')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'location'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Globe className="w-4 h-4" />
          Location
          {(isNational || selectedProvinceId) && (
            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
              {isNational ? 'National' : (provinces as any[]).find((p: any) => p.id === selectedProvinceId)?.code ?? ''}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('services')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'services'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Languages
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('social')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'social'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Share2 className="w-4 h-4" />
          Social
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('sidebar')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sidebar'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Sidebar
          {formData.hiddenSections.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
              {formData.hiddenSections.length} hidden
            </span>
          )}
        </button>
      </div>

      {/* Content editor shortcut — shown for org types that have a dedicated content editor */}
      {(() => {
        const orgTypeKey = (organization as any)?.organizationType?.key;
        const orgTypeName = (organization as any)?.organizationType?.label ?? 'Organization';
        const contentRouteMap: Record<string, string> = {
          'Center-community':         'Center',
          'advocacy-non-charitable':  'advocacy',
          'funeral-services':         'funeral',
          'Pilgrimage-umrah':               'Pilgrimage',
          'financial-services':       'financial',
          'media-publications':       'media',
          'youth-programs':           'youth',
          'charities':                'charity',
          'disability-services':      'disability',
          'healthcare-chaplaincy':    'chaplain',
          'sports-clubs':             'sports',
          'uni-msas':                 'msa',
          'convert-revert-support':   'convert',
          'social-services':          'social-services',
          'weekend-schools':          'weekend-school',
          'Community-schools':          'Community-school',
        };
        const contentSlug = orgTypeKey ? contentRouteMap[orgTypeKey] : null;
        if (!contentSlug) return null;
        return (
          <div className="mb-6 flex items-center gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
            <FileEdit className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-900">{orgTypeName} Profile Content</p>
              <p className="text-xs text-blue-700 mt-0.5">
                Edit the public-facing sections of this organization's profile.
              </p>
            </div>
            <Link
              to={`/admin/organizations/${organizationId}/content/${contentSlug}`}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
            >
              <FileEdit className="w-4 h-4" />
              Edit Content
            </Link>
          </div>
        );
      })()}

      <form id="organization-form" onSubmit={handleSubmit} className="space-y-6">

        {/* Details Tab */}
        {activeTab === 'details' && <>

        {/* Basic Info */}
        <FormSection title="Basic Info">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">OID</label>
              <input
                type="text"
                value={formData.oid}
                onChange={(e) => handleChange('oid', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="ORG-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.name ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="e.g., Community Center of Toronto"
                required
              />
              {errors.name && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />{errors.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.slug ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="Community-center-toronto"
                required
              />
              {errors.slug && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />{errors.slug}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">Must be unique across all organizations</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.organizationTypeId}
                onChange={(e) => handleChange('organizationTypeId', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.organizationTypeId ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                required
              >
                <option value="">Select Type</option>
                {(organizationTypes as any[]).map((type: any) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
              {errors.organizationTypeId && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />{errors.organizationTypeId}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Parent Organization</label>
              <select
                value={formData.parentOrganizationId}
                onChange={(e) => handleChange('parentOrganizationId', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              >
                <option value="">None</option>
                {parentOrganizations.map((org: any) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Optional: select if this is a sub-organization</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="Provide a description of the organization..."
            />
            <p className="text-xs text-gray-500 mt-1">{formData.description.length} characters</p>
          </div>
        </FormSection>

        {/* Media Details — only for media organization type */}
        {isMediaType && <FormSection title="Media Details" description="Additional details for media organizations.">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Scope</label>
              <input
                type="text"
                value={formData.mediaScope}
                onChange={(e) => handleChange('mediaScope', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="National"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Type</label>
              <input
                type="text"
                value={formData.subType}
                onChange={(e) => handleChange('subType', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Digital Magazine"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
              <input
                type="text"
                value={formData.format}
                onChange={(e) => handleChange('format', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Website + Newsletter"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <input
                type="text"
                value={formData.language}
                onChange={(e) => handleChange('language', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="English"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Additional notes or context..."
              />
            </div>
          </div>
        </FormSection>}

        {/* Charity / Social Services */}
        {isCharityOrSocial && <FormSection title="Charity Details" description="Additional details for charitable and social service organizations.">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">CRA Registration Number</label>
            <input
              type="text"
              value={formData.craRegistration}
              onChange={(e) => handleChange('craRegistration', e.target.value)}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="123456789 RR 0001"
            />
          </div>
        </FormSection>}

        {/* Funeral & Burial Services */}
        {isFuneralType && <FormSection title="Funeral Service Details" description="Additional details for funeral and burial service organizations.">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Phone</label>
            <input
              type="tel"
              value={formData.emergencyPhone}
              onChange={(e) => handleChange('emergencyPhone', e.target.value)}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="+1 (416) 555-0100"
            />
          </div>
        </FormSection>}

        {/* Media & Publications */}
        {isMediaPublicationsType && <FormSection title="Media & Publications Details" description="Additional details for media and publication organizations.">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {FORMAT_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleMultiToggle('typeFormat', opt)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    formData.typeFormat.includes(opt)
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
            {formData.typeFormat.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">Selected: {formData.typeFormat.join(', ')}</p>
            )}
          </div>
        </FormSection>}

        {/* Pilgrimage & Umrah Agencies */}
        {isPilgrimageType && <FormSection title="Pilgrimage & Umrah Details" description="Additional details for Pilgrimage and Umrah agencies.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Licenses & Accreditation</label>
              <input
                type="text"
                value={formData.licensesAccreditation}
                onChange={(e) => handleChange('licensesAccreditation', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="IATA, TICO, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Package Types</label>
              <textarea
                value={formData.packageTypes}
                onChange={(e) => handleChange('packageTypes', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Economy Package, Premium Package, Group Package..."
              />
              <p className="text-xs text-gray-500 mt-1">List each package type on a separate line or separated by commas</p>
            </div>
          </div>
        </FormSection>}

        {/* Weekend Community Schools */}
        {isWeekendSchoolType && <FormSection title="Weekend School Details" description="Additional details for weekend Community schools and education programs.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Programs Offered</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PROGRAMS_OFFERED_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleMultiToggle('programsOffered', opt)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.programsOffered.includes(opt)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {AGE_RANGE_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleMultiToggle('ageRange', opt)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.ageRange.includes(opt)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration / Schedule</label>
              <input
                type="text"
                value={formData.durationSchedule}
                onChange={(e) => handleChange('durationSchedule', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Saturdays 9am–1pm"
              />
            </div>
          </div>
        </FormSection>}

        {/* Youth Programs & Camps */}
        {isYouthProgramType && <FormSection title="Youth Program Details" description="Additional details for youth programs and camps.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Program Type</label>
              <input
                type="text"
                value={formData.programType}
                onChange={(e) => handleChange('programType', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Summer Camp, After-School, Weekend Program"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {AGE_RANGE_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleMultiToggle('ageRange', opt)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.ageRange.includes(opt)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </FormSection>}

        {/* Community Schools (Full-Time) */}
        {isCommunitySchoolType && <FormSection title="School Hours" description="Set operating hours for each day of the week.">
          <div className="space-y-2">
            {DAYS_OF_WEEK.map(day => (
              <div key={day} className="flex items-center gap-4">
                <label className="flex items-center gap-2 w-32 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.schoolHours[day]?.enabled ?? false}
                    onChange={(e) => handleSchoolHoursChange(day, 'enabled', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">{day.slice(0, 3)}</span>
                </label>
                {formData.schoolHours[day]?.enabled ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={formData.schoolHours[day]?.start ?? '08:00'}
                      onChange={(e) => handleSchoolHoursChange(day, 'start', e.target.value)}
                      className="px-2 py-1.5 border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-gray-400 text-sm">to</span>
                    <input
                      type="time"
                      value={formData.schoolHours[day]?.end ?? '16:00'}
                      onChange={(e) => handleSchoolHoursChange(day, 'end', e.target.value)}
                      className="px-2 py-1.5 border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring"
                    />
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">Closed</span>
                )}
              </div>
            ))}
          </div>
        </FormSection>}

        {/* Community Financial Services */}
        {isFinancialType && <FormSection title="Community Finance Details" description="Additional details for Community financial service organizations.">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Community Finance Model</label>
              <input
                type="text"
                value={formData.CommunityFinanceModel}
                onChange={(e) => handleChange('CommunityFinanceModel', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Murabaha, Ijara, Musharaka, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Shariah Certification</label>
              <input
                type="text"
                value={formData.shariahCertification}
                onChange={(e) => handleChange('shariahCertification', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="Certified by AAOIFI, ISNA, etc."
              />
            </div>
          </div>
        </FormSection>}

        </>}

        {/* Location Tab */}
        {activeTab === 'location' && (
          <>
          <FormSection title="Location" description="Select the geographic scope for this organization.">
            {/* National toggle */}
            <label className="flex items-center gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50 cursor-pointer">
              <input
                type="checkbox"
                checked={isNational}
                onChange={(e) => handleNationalToggle(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
              />
              <Globe className="w-4 h-4 text-blue-600" />
              <div>
                <span className="text-sm font-medium text-blue-900">National Organization</span>
                <p className="text-xs text-blue-700">This organization operates across Canada and will appear at the country level</p>
              </div>
            </label>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="123 Main St, Toronto, ON"
                />
              </div>

              {/* Province */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Province / Territory</label>
                <select
                  value={selectedProvinceId}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  <option value="">Select Province</option>
                  {(provinces as any[]).map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* City / Municipality */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City / Town</label>
                <select
                  value={selectedMunicipalityId}
                  onChange={(e) => handleMunicipalityChange(e.target.value)}
                  disabled={!selectedProvinceId}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors disabled:opacity-50 disabled:bg-gray-50"
                >
                  <option value="">{selectedProvinceId ? 'Select City / Town' : 'Select a province first'}</option>
                  {(municipalities as any[]).map((m: any) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Neighbourhood */}
              {selectedMunicipalityId && (neighbourhoods as any[]).length > 0 && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Neighbourhood <span className="text-gray-400 font-normal">(optional)</span></label>
                  <select
                    value={selectedNeighbourhoodId}
                    onChange={(e) => handleNeighbourhoodChange(e.target.value)}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  >
                    <option value="">No specific neighbourhood</option>
                    {(neighbourhoods as any[]).map((n: any) => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {(selectedProvinceId || selectedMunicipalityId) && (
                <div className="lg:col-span-2">
                  <p className="text-xs text-gray-500">
                    Selected: {[
                      (provinces as any[]).find((p: any) => p.id === selectedProvinceId)?.name,
                      (municipalities as any[]).find((m: any) => m.id === selectedMunicipalityId)?.name,
                      (neighbourhoods as any[]).find((n: any) => n.id === selectedNeighbourhoodId)?.name
                    ].filter(Boolean).join(' › ')}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => handleChange('postalCode', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="M5V 2T6"
                />
              </div>
            </div>

            {isNational && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-800">This organization will also appear under Canada at the national level.</p>
              </div>
            )}
          </FormSection>

          <FormSection title="Contact" description="Contact details for this organization.">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                <input
                  type="text"
                  value={formData.contact}
                  onChange={(e) => handleChange('contact', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="Jane Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="info@organization.org"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="+1 (416) 555-0100"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  placeholder="https://example.com"
                />
              </div>

            </div>
          </FormSection>
          </>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <>
          <FormSection title="Languages Offered" description="Languages this organization can serve in.">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
              <input
                type="text"
                value={formData.languages.join(', ')}
                onChange={(e) => handleChange('languages', e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="English, French, Native"
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated values</p>
            </div>
          </FormSection>

          </>
        )}

        {/* Social Tab */}
        {activeTab === 'social' && (
          <FormSection title="Social Media" description="Public social channels for this organization.">
            <SocialLinksEditor
              value={formData.socialLinks}
              onChange={(links) => handleChange('socialLinks', links)}
            />
          </FormSection>
        )}

        {/* Sidebar Tab */}
        {activeTab === 'sidebar' && (() => {
          const typeHidden: string[] = ((organization as any).organizationType?.settings as any)?.hiddenSections ?? [];
          return (
            <FormSection
              title="Sidebar Sections"
              description="Control which sections appear in the left sidebar when users view this organization's page."
            >
              {/* Type default info */}
              {typeHidden.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 mb-2">
                  <span className="text-xs text-blue-700 font-medium">Type defaults hidden:</span>
                  {typeHidden.map((k) => (
                    <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
                      {ALL_SIDEBAR_SECTIONS.find((s) => s.key === k)?.label ?? k}
                    </span>
                  ))}
                </div>
              )}
              {typeHidden.length === 0 && (
                <p className="text-xs text-gray-400 mb-2">This organization's type has no default hidden sections.</p>
              )}

              {/* Override toggle */}
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={formData.sidebarOverrideEnabled}
                  onChange={(e) => {
                    handleChange('sidebarOverrideEnabled', e.target.checked);
                    if (e.target.checked && formData.hiddenSections.length === 0) {
                      // Pre-populate with type defaults when enabling override
                      handleChange('hiddenSections', typeHidden);
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Override type defaults for this organization</span>
                  <p className="text-xs text-gray-500">When unchecked, the type's defaults above are used automatically.</p>
                </div>
              </label>

              {/* Per-org section checkboxes — only shown when override is enabled */}
              {formData.sidebarOverrideEnabled && (
                <div className="space-y-5">
                  <div>
                    <h4 className="text-sm font-medium text-gray-800 mb-2">Directory Sections</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {DIRECTORY_SECTIONS.map(({ key, label }) => (
                        <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.hiddenSections.includes(key)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.hiddenSections, key]
                                : formData.hiddenSections.filter((s) => s !== key);
                              handleChange('hiddenSections', next);
                            }}
                            className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500 rounded"
                          />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-800 mb-2">Community Sections</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {COMMUNITY_SECTIONS.map(({ key, label }) => (
                        <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.hiddenSections.includes(key)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.hiddenSections, key]
                                : formData.hiddenSections.filter((s) => s !== key);
                              handleChange('hiddenSections', next);
                            }}
                            className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500 rounded"
                          />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Checked items will not appear in the sidebar. This does not affect global visibility settings.
              </p>
            </FormSection>
          );
        })()}

        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}



